import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import {
  arrayPush,
  Field,
  SubmissionError,
  arrayRemove,
  getFormValues,
  change
} from 'redux-form';
import { isEmpty } from 'lodash';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';
import TableFooter from '@material-ui/core/TableFooter';
import { BoxWrapper } from 'components/Base';
import Forms from 'components/Forms';
import Fields from 'components/Fields';
import { Button, Modal } from 'components/Base';
import Router from 'components/Router/Router';
import {
  formatCurrency,
  unFormatCurrency,
  Notify,
  formatPercent,
  unFormatPercent,
  toFixed,
  errHelper
} from 'utils';
import ManualFund from './ManualFund';
import api from 'api';
import TableComponents from 'components/Table';
import { ModelPortfolioType } from 'store/modules/Meta/types';
import Store from 'store/store';
import ENV from 'consts/env';
import {
  getCashFundIndex,
  checkFundExists,
  fundActiveNotification
} from './utils';
import { FundAllocationType } from 'store/modules/ExistingPolicies/types';
import { initialiseOverrides } from 'components/Policies/utils';
import ModelPortfolioAllocationOptions from 'components/CipManagementv2/ModelPortfolioAllocationOptions';
import { chargeTypeEnum } from 'components/CipManagementv2/components/CipThreshold';
import ModelPortfolioSelector from 'components/CipManagementv2/components/ModelPortfolioSelector';
import Checkbox from 'components/Checkbox/Checkbox';

const {
  FEATURE_FLAGS: { IS_MODEL_PORTFOLIO_CREATOR }
} = ENV;

const { useParams } = Router;
const { connect, selectors } = Store;

const useStyles = makeStyles(() => ({
  footer: {
    fontWeight: 500,
    backgroundColor: '#c6e0f4',
    padding: '0.8rem 1rem',
    color: '#000000de',
    fontSize: '0.8rem'
  },
  btnWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto'
  },
  wrapCash: {
    marginRight: '1rem'
  },
  headerChildren: {
    display: 'flex',
    width: '100%',
    alignItems: 'end',
    justifyContent: 'space-between',
    paddingTop: '0.25rem',
    position: 'relative',
    gap: '1rem'
  }
}));

interface FundsMeta {
  fields?: any;
  meta?: any;
  totalFundValue: number | string;
  allowPercentageValue: boolean;
  wrapAmount: boolean | null;
  aggregateOcfChargeValue: string;
  aggregateFundChargeValue: string;
  aggregateTotalFundChargeValue: string;
  aggregateOneYPerformanceValue: string;
  aggregateThreeYPerformanceValue: string;
  aggregateFiveYPerformanceValue: string;
  formName: string;
  arrayPushField: (form: string, fieldArray: string, value: any) => void;
  modelPortfolioCharges: any[];
  arrayRemove: (form: string, fieldArray: string, index: number) => void;
  formValue: any;
  changeField: (form: string, field: string, value: any) => void;
  isIncomplete?: boolean;
  isComplete?: boolean;
  cashFund: any;
}

const Funds = ({
  fields,
  totalFundValue,
  allowPercentageValue,
  wrapAmount,
  aggregateOcfChargeValue,
  aggregateFundChargeValue,
  aggregateTotalFundChargeValue,
  aggregateOneYPerformanceValue,
  aggregateThreeYPerformanceValue,
  aggregateFiveYPerformanceValue,
  formName,
  arrayPushField,
  meta: { dirty },
  modelPortfolioCharges,
  arrayRemove,
  formValue,
  changeField,
  isIncomplete,
  cashFund
}: FundsMeta) => {
  const [wrapChecked, setIsWrapCash] = useState(wrapAmount ? true : false);
  const [isManualFundModalOpen, setIsManualFundModalOpen] = useState({
    isVisible: false,
    initialValues: {}
  });

  const [modelPortfolioEditor, setModelPortfolioEditor] = useState<{
    isOpen: boolean;
    id: number | null;
    funds: any[];
    name: string | null;
    index: string | null;
    source: 1 | 2 | null;
  }>({
    isOpen: false,
    id: null,
    funds: [],
    name: null,
    index: null,
    source: null
  });
  const [replicateFundFromInvalid, setReplicateFundFromInvalid] = useState({
    isVisible: false,
    initialValues: {}
  });
  const [customPortfolioName, setCustomPortfolioName] = useState('');
  const [customPortfolioFunds, setCustomPortfolioFunds] = useState([]);
  const classes = useStyles();
  const existingFields = fields.getAll();
  const hasValues = existingFields && existingFields.length > 0;
  const { portfolio } = useParams() as any;

  const sumAllValues = (withWrapCash: boolean) => {
    const fundsFiltered = !withWrapCash
      ? existingFields.filter((field: any) => {
          return field.model_portfolio || field.fund.name !== 'Wrap Cash';
        })
      : existingFields;

    return fundsFiltered.reduce(
      (a: any, b: any) => {
        const isModelPortfolioA = a.model_portfolio;
        const isModelPortfolioB = b.model_portfolio;

        const ocfA = Number(
          a[isModelPortfolioA ? 'model_portfolio' : 'fund'].ocf || 0
        );
        const ocfB = Number(
          b[isModelPortfolioB ? 'model_portfolio' : 'fund'].ocf || 0
        );

        const tradingCostA = Number(
          a[isModelPortfolioA ? 'model_portfolio' : 'fund'].trading_costs || 0
        );
        const tradingCostB = Number(
          b[isModelPortfolioB ? 'model_portfolio' : 'fund'].trading_costs || 0
        );

        const totalFundChargeA = Number(
          a[isModelPortfolioA ? 'model_portfolio' : 'fund'].total_fund_charge ||
            0
        );
        const totalFundChargeB = Number(
          b[isModelPortfolioB ? 'model_portfolio' : 'fund'].total_fund_charge ||
            0
        );

        const fundA = a.fund_value ? unFormatCurrency(a.fund_value) : 0;

        const fundB = b.fund_value ? unFormatCurrency(b.fund_value) : 0;
        return {
          totalFundValue: totalFundValue,
          fund_value: toFixed(fundA) + toFixed(fundB),
          fund: {
            ocf: ocfA + ocfB,
            trading_costs: tradingCostA + tradingCostB,
            total_fund_charge: totalFundChargeA + totalFundChargeB
          }
        };
      },
      {
        fund_value: 0,
        fund: {
          ocf: 0,
          trading_costs: 0,
          totalFundChargeB: 0
        }
      }
    );
  };

  const subTotalValues = sumAllValues(false);
  const totalValues = sumAllValues(true);

  const subTotalInPercentage =
    Number(totalFundValue) !== 0
      ? (totalValues.fund_value / Number(totalFundValue)) * 100
      : 0;

  const addNewFund = (fundName: any) => {
    if (!fundName) return;
    if ('fund' in fundName) {
      fundName = fundName.fund;
    }
    if (!fundName.active) return fundActiveNotification();

    const isAlreadyAdded = checkFundExists(existingFields, fundName);
    if (isAlreadyAdded)
      return Notify.warn(
        'Fund already added',
        'Duplicated funds are not allowed'
      );

    const indexToInsert = wrapChecked ? fields.length - 1 : fields.length;

    fields.insert(indexToInsert, {
      fund: fundName,
      fund_value: fundName.fund_value || formatCurrency(0),
      fund_charge_overrides: [
        { fund: fundName, ocf: null, trading_costs: null }
      ],
      fund_value_percentage: 0,
      source_value: 1
    });
  };

  const removeFund = (fundIndex: number) => {
    const wrapCashIndex = fields.length - 1;
    const fundDetails = fields.get(fundIndex);
    const fundId = fundDetails.model_portfolio
      ? fundDetails.model_portfolio.id
      : fundDetails.fund.id;

    const mpChargeIdToRemove = modelPortfolioCharges.findIndex(
      ({ id }: { id: number }) => fundId === id
    );

    if (wrapChecked && wrapCashIndex === fundIndex) {
      fields.remove(fundIndex);
      return setIsWrapCash(false);
    }
    fields.remove(fundIndex);

    // REMOVE MODEL PORTFOLIO CHARGE FROM TABLE
    if (fundDetails.model_portfolio) {
      arrayRemove(formName, 'model_portfolio_charges', mpChargeIdToRemove);
    }
  };

  const hasCashFund = !!checkFundExists(existingFields, cashFund);

  const toggleCashFund = (fields: any, fund: any) => {
    if (hasCashFund) {
      return removeFund(getCashFundIndex(fields));
    }

    return addNewFund(fund);
  };

  const toggleWrapCash = () => {
    if (wrapChecked) {
      removeFund(fields.length - 1);
      return setIsWrapCash(false);
    }

    const wrapCash = {
      fund: {
        name: 'Wrap Cash',
        active: true
      },
      fund_value: formatCurrency(0),
      fund_value_percentage: 0
    };
    fields.insert(fields.length, wrapCash);
    return setIsWrapCash(true);
  };

  const addNewManualFund = (fund: any) => {
    const { id: fundToRemove, fund_value } = fund;

    const shouldReplaceExistingFund = typeof fundToRemove === 'number';
    delete fund.id;

    const preparedFund = {
      ...fund,
      ocf: (Number(unFormatPercent(fund.ocf || 0)) / 100).toFixed(4),
      trading_costs: Number(unFormatPercent(fund.trading_costs) / 100).toFixed(
        4
      ),
      asset_classes:
        fund.asset_classes &&
        fund.asset_classes.map((assetClass: any) => ({
          asset_class: assetClass.asset_class,
          percentage_value: (
            Number(unFormatPercent(assetClass.percentage_value)) / 100
          ).toFixed(4)
        }))
    };

    return api
      .addNewFund({ fundData: preparedFund })
      .then((resp: any) => {
        addNewFund({
          ...resp.data,
          fund_charge_overrides: [
            { fund: fund, ocf: null, trading_costs: null }
          ],
          fund_value: fund_value
        });

        // Remove Fund as it was Recreated
        if (shouldReplaceExistingFund) {
          removeFund(fundToRemove);
        }
        setIsManualFundModalOpen({ isVisible: false, initialValues: {} });
      })
      .catch((err: any) => {
        if (err.response.data.message) {
          Notify.error(err.response.data.message);
        }
        throw new SubmissionError(err.response.data);
      });
  };

  const removeInvalidFund = (oldFundId: number) => {
    const index = fields
      .getAll()
      .findIndex((fund: any) => fund.fund.id === oldFundId);
    fields.remove(index);
    setReplicateFundFromInvalid({ isVisible: false, initialValues: {} });
  };

  const addNewReplicateManualFund = (fund: any) => {
    const { fund_value, oldFundId } = fund;

    const preparedFund = {
      ...fund,
      ocf: (Number(unFormatPercent(fund.ocf || 0)) / 100).toFixed(4),
      trading_costs: Number(unFormatPercent(fund.trading_costs) / 100).toFixed(
        4
      ),
      asset_classes:
        fund.asset_classes &&
        fund.asset_classes.map((assetClass: any) => ({
          asset_class: assetClass.asset_class,
          percentage_value: (
            Number(unFormatPercent(assetClass.percentage_value)) / 100
          ).toFixed(4)
        }))
    };
    return api
      .addNewFund({ fundData: preparedFund })
      .then((resp: any) => {
        addNewFund({
          ...resp.data,
          fund_charge_overrides: [
            { fund: fund, ocf: null, trading_costs: null }
          ],
          fund_value: fund_value
        });
        setReplicateFundFromInvalid({ isVisible: false, initialValues: {} });
        removeInvalidFund(oldFundId);
      })
      .catch((err: any) => {
        if (err.response.data.message) {
          Notify.error(err.response.data.message);
        }
        throw new SubmissionError(err.response.data);
      });
  };

  const addModelPortfolioDefinition = (modelPortfolio: ModelPortfolioType) => {
    api
      .getModelPortfolio({ modelPortfolioId: modelPortfolio.value })
      .then(({ data }: any) => {
        const {
          id,
          name,
          ocf,
          date,
          trading_costs,
          total_fund_charge,
          one_y_performace,
          three_y_performace,
          five_y_performace,
          fund_allocations,
          type
        } = data;
        const indexToInsert = wrapChecked ? fields.length - 1 : fields.length;
        const fakeFund = {
          fund: null,
          model_portfolio_selection: null,
          model_portfolio: {
            active: !!fund_allocations.find((fund: any) => fund.fund?.active),
            id,
            date: date,
            name: name,
            one_y_performace,
            three_y_performace,
            five_y_performace,
            fund_allocations: fund_allocations.map((allocation: any) => ({
              ...allocation,
              fund_value: null
            })),
            ocf,
            trading_costs,
            total_fund_charge,
            type: type ? type : 1
          },
          fund_charge_overrides: [],
          allocation: 0,
          fund_value: formatCurrency(0),
          source_value: 1
        };
        fakeFund.fund_charge_overrides = initialiseOverrides(fakeFund);
        const modelPortolioCharge = {
          name: name,
          cash_value:
            data.mps_charge.charge === 0
              ? null
              : data.mps_charge.charge_type === 1
                ? formatCurrency(data.mps_charge.charge)
                : '-',
          percentage_value:
            data.mps_charge.charge === 0
              ? null
              : data.mps_charge.charge_type === 2
                ? formatPercent(data.mps_charge.charge * 100)
                : '-',
          id: id,
          source_value: data.mps_charge.charge_type
        };

        arrayPushField(
          formName,
          'model_portfolio_charges',
          modelPortolioCharge
        );
        fields.insert(indexToInsert, fakeFund);
      })
      .catch((err: any) => {
        errHelper(err);
      });
  };

  const addModelPortfolio = (modelPortfolio: ModelPortfolioType) => {
    if (!modelPortfolio) return;
    if (modelPortfolio.model_portfolio_selection) {
      api
        .getLatestDefinition({ id: modelPortfolio.value })
        .then(({ data }: any) => {
          addModelPortfolioDefinition({ ...data, value: data.id });
        });

      return;
    }

    addModelPortfolioDefinition({
      ...modelPortfolio,
      value: modelPortfolio.mp_id || modelPortfolio.value
    });
  };

  const calculateModelPortfolioCharge = (data: any) => {
    const monetaryCharge = data.charges.find(
      (charge: any) => charge.charge_type === chargeTypeEnum.MONETARY
    );
    const percentageCharge = data.charges.find(
      (charge: any) => charge.charge_type === chargeTypeEnum.PERCENTAGE
    );

    const cashValue = monetaryCharge.charge || null;
    const percentageValue = cashValue ? null : percentageCharge.charge || null;

    return {
      name: data.name,
      cash_value: cashValue ? formatCurrency(cashValue) : null,
      percentage_value: percentageValue
        ? formatPercent(percentageValue * 100)
        : null,
      id: data.id
    };
  };

  const updateModelPortfolio = (
    data: any,
    id: number,
    pound_values: boolean
  ) => {
    return api
      .updateModelPortfolio({
        portfolio,
        modelPortfolioData: data,
        modelPortfolioId: id
      })
      .then(({ data }) => {
        const indexToInsert = modelPortfolioEditor.index;

        const {
          id,
          name,
          ocf,
          one_y_performace,
          three_y_performace,
          five_y_performace,
          trading_costs,
          total_fund_charge,
          fund_allocations
        } = data;

        const sumFundsValue = fund_allocations.reduce(
          (a: any, b: any) => {
            return {
              fund_value: Number(a.fund_value) + Number(b.fund_value)
            };
          },
          {
            fund_value: 0
          }
        );

        const preparedFund = {
          fund: null,
          model_portfolio_selection: null,
          model_portfolio: {
            id,
            type: 2,
            name,
            one_y_performace,
            three_y_performace,
            five_y_performace,
            fund_allocations,
            ocf,
            trading_costs,
            total_fund_charge
          },
          allocation: 0,
          fund_value: formatCurrency(sumFundsValue.fund_value),
          fund_charge_overrides: [],
          read_only: pound_values,
          source_value: pound_values ? 1 : 2
        };

        preparedFund.fund_charge_overrides = initialiseOverrides(preparedFund);

        fields.splice(indexToInsert, 1, preparedFund);
        const charges = formValue.model_portfolio_charges;
        const chargeIndex = charges.findIndex(
          (charge: any) => charge.id === id
        );
        charges.splice(chargeIndex, 1, calculateModelPortfolioCharge(data));
        changeField(formName, 'model_portfolio_charges', charges);
        setModelPortfolioEditor({
          isOpen: false,
          id: null,
          funds: [],
          name: null,
          index: null,
          source: null
        });
      });
  };

  const saveModelPortfolio = (name: string, funds: any) => {
    const fundData = isIncomplete
      ? funds.filter((fund: any) => !!fund.isSelected)
      : funds;
    const preparedFundAllocation = fundData.map((fundDetails: any) => {
      const fund = isEmpty(fundDetails.fund) ? null : fundDetails.fund;
      const mps = isEmpty(fundDetails.model_portfolio_selection)
        ? null
        : fundDetails.model_portfolio_selection;

      return {
        fund: fund ? { id: fund.id } : {},
        model_portfolio_selection: mps ? { id: mps.id } : {},
        source_value: 2,
        fund_value: null,
        allocation: (
          Number(unFormatPercent(fundDetails.allocation)) / 100
        ).toFixed(4)
      } as any;
    });

    const data = {
      name: name,
      fund_allocations: preparedFundAllocation
    };

    if (modelPortfolioEditor.id) {
      return updateModelPortfolio(data, modelPortfolioEditor.id, false);
    }

    return api
      .createModelPortfolio({
        portfolio: portfolio,
        modelPortfolioData: data
      })
      .then(({ data }) => {
        const indexToInsert = wrapChecked ? fields.length - 1 : fields.length;
        const {
          id,
          name,
          ocf,
          one_y_performace,
          three_y_performace,
          five_y_performace,
          trading_costs,
          total_fund_charge,
          fund_allocations
        } = data;

        const sumFundsValue = fund_allocations.reduce(
          (a: any, b: any) => {
            return {
              fund_value: Number(a.fund_value) + Number(b.fund_value)
            };
          },
          {
            fund_value: 0
          }
        );

        const preparedFund = {
          fund: null,
          model_portfolio_selection: null,
          isSelected: true,
          model_portfolio: {
            id,
            type: 2,
            name: name,
            one_y_performace,
            three_y_performace,
            five_y_performace,
            fund_allocations: fund_allocations,
            ocf,
            trading_costs,
            total_fund_charge
          },
          allocation: 0,
          fund_value: formatCurrency(sumFundsValue.fund_value),
          fund_charge_overrides: [],
          read_only: false,
          source_value: 2
        };

        preparedFund.fund_charge_overrides = initialiseOverrides(preparedFund);

        fields.insert(indexToInsert, preparedFund);

        arrayPushField(
          formName,
          'model_portfolio_charges',
          calculateModelPortfolioCharge(data)
        );

        setModelPortfolioEditor({
          isOpen: false,
          id: null,
          funds: [],
          name: null,
          index: null,
          source: null
        });

        const transferedToMp = funds.reduce((acc: any[], fund: any) => {
          if (fund.isSelected) {
            acc.push(fund.id);
          }
          return acc;
        }, []);
        const oldFields = fields.getAll();
        fields.removeAll();
        let count = 0;

        oldFields.forEach((fund: any) => {
          if (
            (!!fund.fund &&
              !!fund.fund.id &&
              !transferedToMp.includes(fund.fund.id)) ||
            !!fund.model_portfolio
          ) {
            fields.push(fund);
            count += 1;
          }
        });
        const index = wrapChecked ? count - 1 : count;
        fields.insert(index, preparedFund);
        if (wrapChecked) {
          const wrapCash = {
            fund: {
              name: 'Wrap Cash',
              active: true
            },
            fund_value: formatCurrency(wrapAmount),
            fund_value_percentage: 0
          };
          fields.insert(fields.length, wrapCash);
        }
      });
  };

  const getAggregateValue = (value: any) => {
    return !wrapChecked && value;
  };

  const mapAllocations = (data: any) => {
    const totalFundValue = data.reduce(
      (acc: number, fund: FundAllocationType) => {
        if (!!fund.fund && fund.fund.id && !!fund.fund.active) {
          acc += Number(unFormatCurrency(fund.fund_value));
        }
        return acc;
      },
      0
    );

    return data.reduce((acc: any, fundDetails: FundAllocationType) => {
      if (
        !!fundDetails.fund &&
        fundDetails.fund.id &&
        !!fundDetails.fund.active
      ) {
        acc.push({
          allocation:
            fundDetails.fund_value &&
            Number(unFormatCurrency(fundDetails.fund_value)) !== 0
              ? formatPercent(
                  (Number(unFormatCurrency(fundDetails.fund_value)) /
                    totalFundValue) *
                    100
                )
              : null,
          fund_value: fundDetails.fund_value,
          fund: fundDetails.fund,
          model_portfolio_selection: fundDetails.model_portfolio_selection,
          source_value: 2,
          isSelected: true,
          type: fundDetails.fund ? 'f' : 'mp'
        });
      }
      return acc;
    }, []);
  };

  const importPolicyFunds = () => {
    const importedFunds = fields.getAll();

    const extractedImportedFunds = importedFunds.filter(
      (item: any) => !!item.fund
    );

    const newFunds = extractedImportedFunds.filter(
      (importedFund: any) =>
        !customPortfolioFunds.some(
          (existingFund: any) => existingFund.fund?.id === importedFund.id
        )
    );

    if (newFunds.length > 0) {
      const mergedFunds = [...customPortfolioFunds, ...newFunds];
      setCustomPortfolioFunds(mapAllocations(mergedFunds));
      Notify.success('Fund imported successfully');
    } else {
      Notify.warn('No new funds to import');
    }
  };

  useEffect(() => {
    if (modelPortfolioEditor.id) {
      const adjustedFunds = mapAllocations(modelPortfolioEditor.funds);
      setCustomPortfolioName(modelPortfolioEditor.name || '');
      setTimeout(() => {
        setCustomPortfolioFunds(adjustedFunds);
      }, 100);
    } else {
      setCustomPortfolioName('');
      setCustomPortfolioFunds([]);
    }
  }, [modelPortfolioEditor]);

  const [checkedState, setCheckedState] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [allChecked, setAllChecked] = useState(false);

  const handleCheckboxChange = (index: number) => {
    setCheckedState(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleAllCheckboxChange = () => {
    const newCheckedState = existingFields.reduce(
      (acc: any, _: any, index: number) => {
        acc[index] = !allChecked;
        return acc;
      },
      {}
    );
    setCheckedState(newCheckedState);
    setAllChecked(!allChecked);
  };

  const handleDeleteSelectedFunds = () => {
    const selectedIndices = Object.entries(checkedState)
      .filter(([, isChecked]) => isChecked)
      .map(([index]) => parseInt(index));

    // Remove from last to first to keep indices consistent
    selectedIndices
      .sort((a, b) => b - a)
      .forEach(index => {
        removeFund(index);
      });

    setCheckedState({});
    setAllChecked(false);
  };

  return (
    <BoxWrapper
      title=" "
      unrestrictHeaderHeight
      headerChildren={
        <div className={classes.headerChildren}>
          <ModelPortfolioAllocationOptions
            canAddFundManually={false}
            canAddCustomPortfolios
            inline
            addInvestment={(imports: any[], type = 'f') => {
              imports.forEach((item: any) => {
                if (type === 'f') {
                  addNewFund(item);
                } else {
                  addModelPortfolio(item);
                }
              });
            }}
          />
        </div>
      }
      subHeader={
        <>
          {IS_MODEL_PORTFOLIO_CREATOR && (
            <div style={{ marginRight: '1.5rem' }}>
              <Button
                color="primary"
                id="create-model-portfolio"
                type="button"
                onClick={() => {
                  setModelPortfolioEditor({
                    isOpen: true,
                    id: null,
                    funds: [],
                    name: null,
                    index: null,
                    source: null
                  });
                }}
              >
                Create Custom Portfolio
              </Button>
            </div>
          )}
          <div style={{ marginRight: '1.5rem' }}>
            <Button
              color="primary"
              type="button"
              onClick={() =>
                setIsManualFundModalOpen({
                  ...isManualFundModalOpen,
                  isVisible: true
                })
              }
              tooltip="A fund can be added manually when not identified using the adjacent search box."
            >
              Add fund manually
            </Button>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex' }}>
            <div className={classes.btnWrapper}>
              <div className={classes.wrapCash}>
                <Fields.CheckboxCustom
                  isChecked={hasCashFund}
                  label="Pound sterling"
                  onChange={() => toggleCashFund(fields, cashFund)}
                />
              </div>
            </div>
            <div className={classes.btnWrapper}>
              <div className={classes.wrapCash}>
                <Fields.WrapCash
                  toggleWrapCash={toggleWrapCash}
                  wrapChecked={wrapChecked}
                  tooltip="Any amounts assigned to wrap cash are excluded from all risk assessments and asset allocation"
                />
              </div>
            </div>
          </div>
        </>
      }
    >
      <Grid container>
        {hasValues && (
          <>
            <div
              style={{
                marginLeft: 'auto',
                paddingRight: '1rem',
                paddingTop: '1rem'
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handleDeleteSelectedFunds}
                disabled={!Object.values(checkedState).some(v => v)}
              >
                Remove Selected
              </Button>
            </div>
            <Table>
              <TableComponents.TableHead
                cells={[
                  <Checkbox
                    key="select-all-checkbox"
                    onClick={handleAllCheckboxChange}
                    isChecked={allChecked}
                  />,
                  'Fund',
                  'Sector',
                  '£',
                  '%',
                  'OCF',
                  'Transaction cost',
                  'Total Fund Charge',
                  '1yr',
                  '3yr',
                  '5yr',
                  ''
                ]}
              />
              <TableComponents.TableBody>
                {fields.map((fund: any, index: number) => {
                  const fundValues = fields.get(index);
                  const isWrapCashAlone = wrapChecked && fields.length === 1;
                  const isTotalFundsEmpty = Number(totalFundValue) === 0;

                  const calculatePercentageValue = () => {
                    if (isTotalFundsEmpty) return formatPercent(0);

                    if (fundValues.allocation && !dirty) {
                      return formatPercent(Number(fundValues.allocation) * 100);
                    }

                    const calculatedValue =
                      unFormatCurrency(fundValues.fund_value) /
                      Number(totalFundValue);

                    return formatPercent(
                      Number(calculatedValue.toFixed(4)) * 100
                    );
                  };

                  const percentageValue = calculatePercentageValue();

                  return (
                    <Forms.ManagePolicies.Fund
                      fund={fund}
                      key={index}
                      formName={formName}
                      fundId={index}
                      removeFund={removeFund}
                      allowPercentage={allowPercentageValue}
                      fundValues={fundValues}
                      percentageValue={percentageValue}
                      subTotalValues={subTotalValues}
                      isWrapCashAlone={isWrapCashAlone}
                      aggregateOcfChargeValue={aggregateOcfChargeValue}
                      aggregateFundChargeValue={aggregateFundChargeValue}
                      aggregateTotalFundChargeValue={
                        aggregateTotalFundChargeValue
                      }
                      aggregateOneYPerformanceValue={
                        aggregateOneYPerformanceValue
                      }
                      aggregateThreeYPerformanceValue={
                        aggregateThreeYPerformanceValue
                      }
                      aggregateFiveYPerformanceValue={
                        aggregateFiveYPerformanceValue
                      }
                      setModelPortfolioEditor={setModelPortfolioEditor}
                      isIncomplete={isIncomplete}
                      setIsManualFundModalOpen={setIsManualFundModalOpen}
                      setReplicateFundFromInvalid={setReplicateFundFromInvalid}
                      removeInvalidFund={removeInvalidFund}
                      getAggregateValue={getAggregateValue}
                      isChecked={!!checkedState[index]}
                      onToggleCheck={() => handleCheckboxChange(index)}
                    />
                  );
                })}
              </TableComponents.TableBody>
              <TableFooter>
                <TableRow>
                  <TableComponents.TableCell
                    style={{ width: '10px' }}
                    className={classes.footer}
                  ></TableComponents.TableCell>
                  <TableComponents.TableCell
                    width="200px"
                    className={classes.footer}
                  >
                    Total
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.footer} />
                  <TableComponents.TableCell className={classes.footer}>
                    {formatCurrency(totalValues.fund_value)}
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.footer}>
                    {formatPercent(subTotalInPercentage)}
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.footer}>
                    {getAggregateValue(aggregateOcfChargeValue)}
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.footer}>
                    {getAggregateValue(aggregateFundChargeValue)}
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.footer}>
                    {getAggregateValue(aggregateTotalFundChargeValue)}
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.footer}>
                    {getAggregateValue(aggregateOneYPerformanceValue)}
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.footer}>
                    {getAggregateValue(aggregateThreeYPerformanceValue)}
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.footer}>
                    {getAggregateValue(aggregateFiveYPerformanceValue)}
                  </TableComponents.TableCell>
                  <TableComponents.TableCell className={classes.footer} />
                </TableRow>
              </TableFooter>
            </Table>
          </>
        )}
      </Grid>
      <Field name="settings.funds.errors" component={Fields.Warn} />

      <Modal
        closeModalFunc={() =>
          setIsManualFundModalOpen({ isVisible: false, initialValues: {} })
        }
        isOpen={isManualFundModalOpen.isVisible}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <ManualFund
          onSubmit={addNewManualFund}
          closeModalFunc={() =>
            setIsManualFundModalOpen({ isVisible: false, initialValues: {} })
          }
          initialValues={isManualFundModalOpen.initialValues}
        />
      </Modal>

      <Modal
        isOpen={replicateFundFromInvalid.isVisible}
        closeModalFunc={() =>
          setReplicateFundFromInvalid({ isVisible: false, initialValues: {} })
        }
      >
        <ManualFund
          onSubmit={addNewReplicateManualFund}
          closeModalFunc={() =>
            setReplicateFundFromInvalid({ isVisible: false, initialValues: {} })
          }
          initialValues={replicateFundFromInvalid.initialValues}
        />
      </Modal>

      <ModelPortfolioSelector
        isOpen={modelPortfolioEditor.isOpen}
        closeModelFunc={() =>
          setModelPortfolioEditor({
            isOpen: false,
            id: null,
            funds: [],
            name: null,
            index: null,
            source: null
          })
        }
        investments={[]}
        canHaveHistory={false}
        canLock={false}
        onCreate={saveModelPortfolio}
        option="create"
        defaults={{
          name: customPortfolioName,
          funds: customPortfolioFunds
        }}
      >
        <Button
          onClick={importPolicyFunds}
          id="save-model-portfolio"
          style={{ marginLeft: '1rem' }}
        >
          Import Policy Funds
        </Button>
      </ModelPortfolioSelector>
    </BoxWrapper>
  );
};

export default connect(
  (state: any, props: any) => ({
    formValue: getFormValues(props.formName)(state),
    cashFund: selectors.metaSelectors.cashFund(state)
  }),
  { arrayPushField: arrayPush, arrayRemove: arrayRemove, changeField: change }
)(Funds);
